import { zodResolver } from '@hookform/resolvers/zod';
import { type Allocation } from 'components/new/AllocationsManager';
import { AllocationRule } from 'further-types/lp/allocations';
import { TransactionOperation } from 'further-types/lp/transaction';
import { CalendarDay } from 'further-ui/utils';
import { manualJournal as rules } from 'further-ui/validations';
import { round, sumBy } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import checkMutualReconciliationMode from './checkMutualReconciliationMode';
import type { ReconcilableTransaction } from './useReconcilableTransactions';

export type FormValues = z.infer<typeof rules.CreateManualAction>;

const emptyTransaction = {
  transactionTypeId: '',
  amountLcy: 0,
  amountTcy: 0,
  operationType: TransactionOperation.Debit,
};

const defaultValues = {
  description: '',
  ledgerDate: new CalendarDay(new Date()),
  counterparty: {
    id: undefined,
    name: '',
  },
  allocations: [],
  transactions: [emptyTransaction, emptyTransaction],
  allocationRule: AllocationRule.ByCommitmentAndClosingDate,
  totalAmountTcy: 0,
  totalAmountLcy: 0,
};

export default function useManualActionForm({
  manualAction,
  reconcilableTransactions,
}: {
  manualAction?: FormValues;
  reconcilableTransactions: Array<ReconcilableTransaction>;
}) {
  const { control, watch, formState, setValue, handleSubmit, getValues } =
    useForm<FormValues>({
      shouldFocusError: true,
      resolver: zodResolver(rules.CreateManualAction),
      defaultValues,
      values: manualAction ?? defaultValues,
      criteriaMode: 'all',
      resetOptions: {
        keepDefaultValues: false,
      },
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'transactions',
  });
  const formValues = watch();

  const handleAddTransaction = () => {
    append(emptyTransaction);
  };

  const handleRemoveTransaction = (index: number) => {
    remove(index);
  };

  const handleAllocationRuleChange = (allocationRule: AllocationRule) => {
    setValue('allocationRule', allocationRule);
  };
  const handleAllocationsChange = (allocations: Array<Allocation>) => {
    setValue('allocations', allocations);
  };

  const setTotalAmounts = useCallback(() => {
    const totalAmountLcy = sumBy(
      formValues.transactions.filter(
        (t) => t.operationType === TransactionOperation.Debit,
      ),
      (transaction) => transaction.amountLcy,
    );
    const totalAmountTcy = sumBy(
      formValues.transactions.filter(
        (t) => t.operationType === TransactionOperation.Debit,
      ),
      (transaction) => transaction.amountTcy,
    );

    setValue('totalAmountLcy', round(totalAmountLcy, 2));
    setValue('totalAmountTcy', round(totalAmountTcy, 2));
  }, [formValues.transactions, setValue]);

  useEffect(() => {
    setTotalAmounts();
  }, [formValues.transactions, setTotalAmounts]);

  const handleTransactionTypeChange = (index: number, value: string) => {
    setValue(`transactions.${index}.transactionTypeId`, value);
  };

  const handleDebitAmountChange = (index: number, value: number) => {
    setValue(`transactions.${index}.amountLcy`, value);
    setValue(`transactions.${index}.amountTcy`, value);
    setValue(`transactions.${index}.operationType`, TransactionOperation.Debit);
    setTotalAmounts();
  };

  const handleCreditAmountChange = (index: number, value: number) => {
    setValue(`transactions.${index}.amountLcy`, value);
    setValue(`transactions.${index}.amountTcy`, value);
    setValue(
      `transactions.${index}.operationType`,
      TransactionOperation.Credit,
    );
    setTotalAmounts();
  };

  useEffect(() => {
    const handleReconcilableTransactionRowsSelection = () => {
      const selectedReconcilableTransactions = reconcilableTransactions.filter(
        (t) => t.isSelected,
      );

      const isInMutualReconciliationMode = checkMutualReconciliationMode(
        selectedReconcilableTransactions,
      );
      setValue('isInMutualReconciliationMode', isInMutualReconciliationMode);

      if (selectedReconcilableTransactions.length === 0) {
        setValue('transactions', [emptyTransaction, emptyTransaction]);
        setValue('allocationRule', AllocationRule.ByCommitmentAndClosingDate);
        setValue('allocations', []);
        return;
      }

      if (isInMutualReconciliationMode) {
        const transactions = selectedReconcilableTransactions.map(
          (selectedTransaction) => ({
            reconcilableTransactionId: selectedTransaction.transactionId,
            transactionTypeId: selectedTransaction.transactionTypeId,
            amountTcy: selectedTransaction.unreconciledAmountTcy,
            amountLcy: selectedTransaction.unreconciledAmountTcy,
            operationType:
              selectedTransaction.operationType === TransactionOperation.Credit
                ? TransactionOperation.Debit
                : TransactionOperation.Credit,
          }),
        );

        setValue('transactions', transactions);
        setValue('allocationRule', AllocationRule.Reconciliation);
        setValue('allocations', []);
        return;
      }

      const transactions = selectedReconcilableTransactions.flatMap(
        (selectedTransaction) => {
          const existingFormTransaction = getValues('transactions').find(
            (t) =>
              t.reconcilableTransactionId ===
                selectedTransaction.transactionId &&
              t.operationType === selectedTransaction.operationType,
          );
          return [
            {
              reconcilableTransactionId: selectedTransaction.transactionId,
              transactionTypeId: selectedTransaction.transactionTypeId,
              amountTcy: selectedTransaction.unreconciledAmountTcy,
              amountLcy: selectedTransaction.unreconciledAmountTcy,
              operationType:
                selectedTransaction.operationType ===
                TransactionOperation.Credit
                  ? TransactionOperation.Debit
                  : TransactionOperation.Credit,
            },
            {
              reconcilableTransactionId: selectedTransaction.transactionId,
              transactionTypeId:
                existingFormTransaction?.transactionTypeId ?? '',
              amountTcy: selectedTransaction.unreconciledAmountTcy,
              amountLcy: selectedTransaction.unreconciledAmountTcy,
              operationType: selectedTransaction.operationType,
            },
          ];
        },
      );

      setValue('transactions', transactions);
      setValue('allocationRule', AllocationRule.Reconciliation);
      setValue('allocations', []);
    };
    handleReconcilableTransactionRowsSelection();
  }, [reconcilableTransactions, setValue, getValues]);

  const transactionsError = formState.errors.transactions?.message;

  const formTransactions = fields.map((field, index) => ({
    fieldId: field.id,
    ...formValues.transactions[index],
    reconcilableTransaction: reconcilableTransactions.find(
      (t) =>
        t.transactionId ===
        formValues.transactions[index].reconcilableTransactionId,
    ),
  }));

  return {
    control,
    formValues,
    formState,
    formTransactions,
    handleAddTransaction,
    handleRemoveTransaction,
    handleAllocationRuleChange,
    handleAllocationsChange,
    handleTransactionTypeChange,
    handleDebitAmountChange,
    handleCreditAmountChange,
    transactionsError,
    handleSubmit,
  };
}
